import { Link } from "react-router-dom";
import Marquee from "react-fast-marquee";
import { motion } from "framer-motion";
import { ArrowUpRight, Lock } from "lucide-react";
import { COMPANY, HERO_TICKER, SERVICES, SCALE_NUMBERS } from "../lib/content";
import { Reveal, MaskReveal } from "../components/Reveal";
import LogoMarquee from "../components/LogoMarquee";
import Collage from "../components/Collage";
import ClientFilmStrip from "../components/ClientFilmStrip";

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO — magazine cover treatment over cinematic loop */}
      <section className="relative h-[100svh] min-h-[640px] w-full overflow-hidden bg-ink text-cream">
        <video
          className="absolute inset-0 w-full h-full object-cover"
          autoPlay
          muted
          loop
          playsInline
         
          data-testid="hero-video"
        >
          <source src="/hero-kling.webm" type="video/webm" />
          <source src="/hero-kling-r.mp4" type="video/mp4" />
        </video>
        {/* Light scrim — left-leaning for readability, lets the video breathe on the right */}
        <div className="absolute inset-0 bg-gradient-to-r from-ink/65 via-ink/15 to-transparent" />
        {/* Subtle bottom vignette for depth */}
        <div className="absolute inset-0 bg-gradient-to-t from-ink/30 via-transparent to-ink/15" />
        {/* Faint grain */}
        <div className="absolute inset-0 opacity-[0.07] pointer-events-none mix-blend-overlay" style={{ backgroundImage: "url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%22220%22 height=%22220%22><filter id=%22n%22><feTurbulence type=%22fractalNoise%22 baseFrequency=%220.9%22 numOctaves=%222%22 stitchTiles=%22stitch%22/></filter><rect width=%22100%25%22 height=%22100%25%22 filter=%22url(%23n)%22/></svg>')" }} />

        {/* Side vertical rail (desktop only) */}
        <div className="absolute top-1/2 right-8 md:right-12 -translate-y-1/2 z-10 hidden md:block">
          <div className="text-cream/55 overline tracking-[0.35em] writing-mode-vertical" style={{ writingMode: "vertical-rl", textOrientation: "mixed" }}>
            M·MEDIA · COVER STORY · LIVE ARRIVAL
          </div>
        </div>

        {/* Floating eyebrow — pinned to the very top of the hero so it doesn't cover the newspaper */}
        <div className="absolute top-20 md:top-24 left-0 right-0 z-10 container-edge">
          <div className="inline-block">
            <div
              className="text-cream text-[9px] uppercase font-medium"
              style={{ letterSpacing: "0.22em", textAlign: "justify", textAlignLast: "justify" }}
              data-testid="hero-tagline"
            >
              Media House for Visionary Women
            </div>
            <div className="overline text-cream/75 mt-0.5" data-testid="hero-eyebrow">
              Issue 26 · A Newport Beach Atelier
            </div>
          </div>
          <span className="hidden md:block overline text-cream/70 absolute top-3 right-0 container-edge-right">
            Established 2016 · Meghan Amaral, CEO
          </span>
        </div>

        <div className="relative z-10 h-full container-edge flex flex-col justify-end pb-16 md:pb-36 pt-24 md:pt-28">
          <MaskReveal>
            <h1
              className="font-display uppercase text-cream leading-[0.82] text-[12vw] md:text-[11vw] font-black tracking-[-0.02em]"
              data-testid="hero-headline"
            >
              The Cover<br/>Story is<br/>
              <span className="relative inline-block serif-italic font-serif normal-case text-cream tracking-normal">
                you.
                {/* Red carpet accent line under "you." */}
                <span aria-hidden className="absolute left-0 -bottom-2 md:-bottom-4 h-[6px] md:h-[10px] w-full bg-[#C8102E]" />
              </span>
            </h1>
          </MaskReveal>

          <Reveal delay={0.4} className="mt-7 md:mt-14 max-w-xl">
            <p className="font-serif text-lg md:text-2xl leading-snug text-cream">
              A full-service media house elevating women, founders, and houses into the names the world repeats. <span className="serif-italic">By application only.</span>
            </p>
            <div className="flex items-center gap-3 mt-5 md:mt-8 flex-wrap">
              <Link to="/apply" className="btn-ink bg-cream text-ink border-cream hover:bg-transparent hover:text-cream" data-testid="hero-cta-contact">
                Request an application <ArrowUpRight size={16} />
              </Link>
              <Link to="/studio" className="btn-ghost border-cream/60 text-cream hover:bg-cream hover:text-ink" data-testid="hero-cta-studio">
                Visit the Studio
              </Link>
              <Link to="/give" className="overline text-cream/80 hover:text-cream" data-testid="hero-cta-give">
                10% returns to charity →
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* TICKER */}
      <div className="bg-cream border-y border-ink/15 py-6 relative overflow-hidden">
        <Marquee gradient={false} speed={60} direction="right">
          <span className="ticker-text text-ink text-3xl md:text-5xl mx-10">{HERO_TICKER}</span>
          <span className="ticker-text text-ink text-3xl md:text-5xl mx-10 serif-italic font-serif normal-case lowercase">a love letter to the front page</span>
          <span className="ticker-text text-ink text-3xl md:text-5xl mx-10">{HERO_TICKER}</span>
        </Marquee>
      </div>

      {/* PRESS / NETWORK LOGOS — runs left to counter the cream ticker above */}
      <LogoMarquee variant="press" direction="left" />

      {/* EDITORIAL MOMENT — THE FRONT ROW (red carpet) */}
      <section className="relative w-full overflow-hidden bg-ink" data-testid="front-row-moment">
        <img
          src="/hero-redcarpet.jpg"
          alt="The front row — paparazzi arrival"
          loading="lazy"
          decoding="async"
          className="w-full h-[70vh] md:h-[88vh] object-cover object-center"
        />
        {/* Dark gradient on the right for the quote */}
        <div className="absolute inset-0 bg-gradient-to-l from-ink/85 via-ink/30 to-transparent" />
        <div className="absolute inset-0 container-edge flex items-center justify-end">
          <div className="text-cream max-w-xl text-right">
            <div className="overline text-cream/70 mb-5">Editorial 02 — The Front Row</div>
            <MaskReveal delay={0.05}>
              <p className="font-display uppercase text-5xl md:text-7xl leading-[0.9]">
                Built for the women <br/>
                <span className="serif-italic font-serif normal-case">who arrive bright</span>.
              </p>
            </MaskReveal>
            <Reveal delay={0.2}>
              <p className="font-serif italic text-lg md:text-2xl mt-8 text-cream/85 leading-relaxed">
                The flashbulbs are the easy part. <br/>
                The legacy underneath them is what we engineer.
              </p>
              <p className="overline text-cream/55 mt-7">— Meghan Amaral, M Media Corp</p>
            </Reveal>
          </div>
        </div>
        {/* Top + bottom rules for editorial framing */}
        <div className="absolute top-0 left-0 right-0 h-px bg-cream/15" />
        <div className="absolute bottom-0 left-0 right-0 h-px bg-cream/15" />
      </section>

      {/* BY INVITATION BAND */}
      <section className="bg-cream py-20 md:py-28 border-b border-ink/15" data-testid="by-invitation">
        <div className="container-edge grid grid-cols-12 gap-y-8 gap-x-6 items-center">
          <div className="col-span-12 md:col-span-7">
            <div className="overline text-ink/55 inline-flex items-center gap-2"><Lock size={12} /> Roster · 2026 — By Invitation</div>
            <Reveal>
              <h2 className="font-display uppercase text-6xl md:text-9xl leading-[0.85] mt-4">
                We accept<br/>by <span className="serif-italic font-serif normal-case">application</span>.
              </h2>
            </Reveal>
          </div>
          <div className="col-span-12 md:col-span-5">
            <Reveal delay={0.15}>
              <p className="text-lg md:text-xl text-ink/80 leading-relaxed font-light">
                Our roster runs lean by design. Each season we accept a small number of new partners — founders, talent and houses prepared for the velocity our work creates. Apply, interview, partner.
              </p>
              <Link to="/apply" className="btn-ink mt-8 inline-flex" data-testid="invitation-apply">
                Begin the application <ArrowUpRight size={14} />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section className="container-edge py-24 md:py-40 grid grid-cols-12 gap-y-12 gap-x-6 relative">
        <Collage src="/assets/star.jpg" w={56} top="40px" right="4%" rotate={-14} blend="multiply" opacity={0.55} />
        <div className="col-span-12 md:col-span-5">
          <div className="overline text-ink/60">Index 01 — Manifesto</div>
          <Reveal delay={0.1}>
            <p className="serif-italic font-serif text-3xl md:text-5xl leading-[1.05] mt-6">
              We don&apos;t chase trends. We <span className="font-display not-italic uppercase">set</span> the terms by which they are remembered.
            </p>
          </Reveal>
        </div>
        <div className="col-span-12 md:col-span-6 md:col-start-7 self-end">
          <Reveal delay={0.2}>
            <p className="text-lg md:text-xl text-ink/80 leading-relaxed font-light">
              M Media Corp is a partnership-led studio. From editorial campaigns and films to the holographic skylines we&apos;ll deploy in 2028, every output is engineered for women and houses who already know — they just need it heard.
            </p>
            <Link to="/about" className="btn-ghost mt-8" data-testid="intro-about-cta">
              Read our story <ArrowUpRight size={14} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* SERVICES PREVIEW — bento */}
      <section className="bg-ink text-cream py-24 md:py-32" data-testid="services-preview">
        <div className="container-edge">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-16">
            <div>
              <div className="overline text-cream/50">Index 02 — Divisions</div>
              <h2 className="font-display uppercase text-7xl md:text-9xl leading-[0.85] mt-4">Nine ways<br/>to <span className="serif-italic font-serif normal-case">be told</span>.</h2>
            </div>
            <Link to="/services" className="btn-ghost border-cream/60 text-cream hover:bg-cream hover:text-ink" data-testid="services-preview-cta">
              All disciplines <ArrowUpRight size={14} />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-cream/15">
            {SERVICES.map((s, i) => (
              <Reveal key={s.slug} delay={i * 0.04}>
                <Link
                  to="/services"
                  className="group block bg-ink p-8 md:p-10 h-full hover:bg-cream hover:text-ink transition-colors duration-500"
                  data-testid={`service-card-${s.slug}`}
                >
                  <div className="flex items-start justify-between">
                    <span className="overline text-cream/50 group-hover:text-ink/50">{s.number}</span>
                    <ArrowUpRight size={18} className="opacity-50 group-hover:opacity-100 group-hover:rotate-12 transition-all" />
                  </div>
                  <h3 className="font-display uppercase text-5xl md:text-6xl mt-12 leading-[0.85]">{s.name}</h3>
                  <p className="font-serif italic text-xl mt-4 opacity-80">{s.short}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* STUDIO PREVIEW — replaces Work/Reel for confidentiality */}
      <section className="container-edge py-24 md:py-40" data-testid="studio-preview">
        <div className="flex items-end justify-between flex-wrap gap-6">
          <div>
            <div className="overline text-ink/60">Index 03 — The Studio</div>
            <h2 className="font-display uppercase text-7xl md:text-9xl leading-[0.85] mt-4">
              Take a piece <br/>
              <span className="serif-italic font-serif normal-case">of the house</span>.
            </h2>
            <p className="font-serif text-xl md:text-2xl mt-8 max-w-xl text-ink/75">
              Forty-five editorial-grade workbooks, masterclasses and signature courses — ten of them complimentary, all lifted straight from our agency playbooks.
            </p>
            <Link to="/studio" className="btn-ghost mt-10 inline-flex" data-testid="studio-preview-cta">
              Enter the Studio <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* GIVEBACK BAND — full bleed cinematic */}
      <section className="relative h-[80svh] min-h-[520px] w-full overflow-hidden bg-ink text-cream" data-testid="giveback-band">
        <video
          className="absolute inset-0 w-full h-full object-cover opacity-55"
          src="https://customer-assets.emergentagent.com/job_brand-proposal-site/artifacts/81np4y3d_elevated-cityscape-of-los-angeles-revealing-illuminated-skyline-downtown-skyscraper-SBV-354937341-HD.mp4"
          autoPlay muted loop playsInline preload="auto"
          aria-hidden
        />
        <div className="absolute inset-0 bg-gradient-to-br from-ink/85 via-ink/50 to-ink/70" />
        <div className="relative z-10 h-full container-edge flex flex-col justify-between py-16">
          <div className="flex justify-between overline text-cream/65">
            <span>The House · Giveback</span>
            <span className="hidden md:block">Since day one.</span>
          </div>
          <MaskReveal>
            <h2 className="font-display uppercase leading-[0.82] text-[14vw] md:text-[10vw]">
              Ten percent<br/>
              <span className="serif-italic font-serif normal-case">goes home</span>.
            </h2>
          </MaskReveal>
          <div className="flex items-end justify-between flex-wrap gap-6">
            <p className="font-serif text-xl md:text-2xl max-w-md text-cream/90">
              Every client package returns ten percent to a curated list of eighteen partner organisations — chosen with care, refreshed every year.
            </p>
            <Link to="/give" className="btn-ink bg-cream text-ink border-cream hover:bg-transparent hover:text-cream" data-testid="giveback-band-cta">
              See the partners →
            </Link>
          </div>
        </div>
      </section>

      {/* SCALE — power band */}
      <section className="relative bg-ink text-cream py-24 md:py-32 overflow-hidden" data-testid="scale-band">
        {/* Ambient video backdrop */}
        <video
          className="absolute inset-0 w-full h-full object-cover opacity-25 pointer-events-none"
          src="https://customer-assets.emergentagent.com/job_brand-proposal-site/artifacts/yytb0kwg_night-panorama-of-the-city-SBV-353780195-HD.mp4"
          autoPlay muted loop playsInline preload="auto"
          aria-hidden
        />
        <div className="absolute inset-0 bg-gradient-to-b from-ink/85 via-ink/70 to-ink/95 pointer-events-none" aria-hidden />
        <div className="container-edge relative z-10">
          <div className="grid grid-cols-12 gap-y-12 gap-x-8 items-end">
            <div className="col-span-12 md:col-span-7">
              <div className="overline text-cream/55">The Scale</div>
              <h2 className="font-display uppercase text-7xl md:text-[10vw] leading-[0.82] mt-4">
                Built for<br/><span className="serif-italic font-serif normal-case">consequence</span>.
              </h2>
            </div>
            <div className="col-span-12 md:col-span-5">
              <p className="text-lg md:text-xl text-cream/85 leading-relaxed">
                Every quarter we deliver work at the scale of a holding company — cinema launches, IPO press tours, runway-week takeovers, billion-impression OOH. Built for the founder who needs that reach without the friction of one.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-cream/15 mt-16">
            {SCALE_NUMBERS.map((s) => (
              <div key={s.l} className="bg-ink p-6 md:p-8">
                <div className="font-display text-5xl md:text-7xl leading-none">{s.n}</div>
                <div className="overline text-cream/55 mt-3">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PARTNERS / VENUES MARQUEE */}
      <LogoMarquee variant="partners" />

      {/* NYC ESTABLISHMENT — bicoastal cinematic band */}
      <section className="relative md:h-[80svh] min-h-[520px] w-full overflow-hidden bg-ink text-cream" data-testid="nyc-band">
        <video
          className="absolute inset-0 w-full h-full object-cover opacity-70"
          src="https://customer-assets.emergentagent.com/job_brand-proposal-site/artifacts/2v550kpo_establishment-shot-new-york-city-skyline-urban-business-district-SBV-305232997-HD.mp4"
          autoPlay muted loop playsInline preload="auto"
          aria-hidden
        />
        <div className="absolute inset-0 bg-gradient-to-r from-ink/85 via-ink/40 to-ink/30" aria-hidden />

        {/* Layered Polaroids — stacked: Julia Post Guenther (top, Newport Beach) over Susan Grau (NYC) */}
        <div
          className="hidden md:flex absolute right-[3%] top-1/2 -translate-y-1/2 z-20 flex-col items-end gap-4"
          data-testid="bicoastal-polaroid-stack"
        >
          <motion.figure
            initial={{ opacity: 0, y: -30, rotate: -3 }}
            whileInView={{ opacity: 1, y: 0, rotate: -3 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.9, ease: "easeOut", delay: 0.1 }}
            className="bg-cream p-3 pb-12 shadow-2xl relative mr-8"
            style={{ width: "260px" }}
            data-testid="newport-polaroid"
          >
            <img
              src="/polaroids/julia-newport.jpg"
              alt="Meghan Amaral with Julia Post Guenther in Newport Beach"
              loading="lazy"
              decoding="async"
              className="w-full aspect-[4/5] object-cover"
            />
            <figcaption className="absolute bottom-1 left-0 right-0 text-center font-handwriting text-ink/90 text-3xl leading-none px-2">
              Julia Post-Guenther
            </figcaption>
          </motion.figure>

          <motion.figure
            initial={{ opacity: 0, y: 30, rotate: 4 }}
            whileInView={{ opacity: 1, y: 0, rotate: 4 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.9, ease: "easeOut", delay: 0.25 }}
            className="bg-cream p-3 pb-12 shadow-2xl"
            style={{ width: "260px" }}
            data-testid="nyc-polaroid"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_brand-proposal-site/artifacts/1l9gzp31_IMG_5231.jpeg"
              alt="Meghan Amaral with Susan Grau in New York City"
              loading="lazy"
              decoding="async"
              className="w-full aspect-[4/5] object-cover"
            />
            <figcaption className="absolute bottom-1 left-0 right-0 text-center font-handwriting text-ink/90 text-4xl leading-none px-2">
              Susan Grau
            </figcaption>
          </motion.figure>
        </div>

        <div className="relative z-10 md:h-full container-edge flex flex-col justify-between py-14 gap-10 md:gap-0">
          <div className="overline text-cream/70">Bureau · Bicoastal</div>
          <MaskReveal>
            <h2 className="font-display uppercase leading-[0.82] text-[14vw] md:text-[9vw] md:max-w-[68%]">
              Newport Beach<br/><span className="serif-italic font-serif normal-case text-cream/95">to New York</span>.
            </h2>
          </MaskReveal>

          {/* Mobile-only polaroid duo — tilted, side by side */}
          <div
            className="md:hidden grid grid-cols-2 gap-3 mt-2"
            data-testid="bicoastal-polaroid-stack-mobile"
          >
            <motion.figure
              initial={{ opacity: 0, y: 20, rotate: -3 }}
              whileInView={{ opacity: 1, y: 0, rotate: -3 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.7, ease: "easeOut", delay: 0.05 }}
              className="bg-cream p-2 pb-14 shadow-2xl relative"
              data-testid="newport-polaroid-mobile"
            >
              <img
                src="/polaroids/julia-newport.jpg"
                alt="Meghan Amaral with Julia Post Guenther in Newport Beach"
                loading="lazy"
                decoding="async"
                className="w-full aspect-[4/5] object-cover"
              />
              <figcaption className="absolute bottom-1 left-0 right-0 text-center font-handwriting text-ink/90 text-2xl leading-tight px-1">
                Julia Post-Guenther
              </figcaption>
            </motion.figure>

            <motion.figure
              initial={{ opacity: 0, y: 20, rotate: 3 }}
              whileInView={{ opacity: 1, y: 0, rotate: 3 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.7, ease: "easeOut", delay: 0.15 }}
              className="bg-cream p-2 pb-14 shadow-2xl relative"
              data-testid="nyc-polaroid-mobile"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_brand-proposal-site/artifacts/1l9gzp31_IMG_5231.jpeg"
                alt="Meghan Amaral with Susan Grau in New York City"
                loading="lazy"
                decoding="async"
                className="w-full aspect-[4/5] object-cover"
              />
              <figcaption className="absolute bottom-1 left-0 right-0 text-center font-handwriting text-ink/90 text-3xl leading-tight px-1">
                Susan Grau
              </figcaption>
            </motion.figure>
          </div>

          <div className="grid grid-cols-12 gap-y-4 gap-x-8 items-end md:max-w-[68%]">
            <p className="col-span-12 md:col-span-7 font-serif text-xl md:text-2xl text-cream/90 leading-snug">
              Two coasts, one masthead. Wherever the story breaks, we are already on the floor.
            </p>
            <span className="col-span-12 md:col-span-5 overline text-cream/60 md:text-right">
              Newport Beach · Los Angeles · New York
            </span>
          </div>
        </div>
      </section>

      {/* AND EVERYWHERE IN BETWEEN — Client film-strip */}
      <section className="bg-cream py-14 md:py-20 border-b border-ink/10">
        <div className="container-edge">
          <ClientFilmStrip testid="home-client-filmstrip" />
        </div>
      </section>

      {/* QUOTE BAND */}
      <section className="bg-cream border-y border-ink/15 py-24 md:py-32 relative overflow-hidden">
        <div className="container-edge max-w-5xl">
          <Reveal>
            <p className="serif-italic font-serif text-4xl md:text-6xl leading-[1.05]">
              &ldquo;Working with M Media is like hiring an editor-in-chief, a publicist, and a director — and asking them to agree.&rdquo;
            </p>
          </Reveal>
        </div>
      </section>

      {/* JOURNAL */}
      {/* Journal section moved to /journal — kept off the home page on purpose. */}

      {/* CLOSING — By Application editorial close-out */}
      <section className="relative h-[80svh] min-h-[520px] w-full overflow-hidden bg-ink text-cream" data-testid="closing-band">
        <video
          className="absolute inset-0 w-full h-full object-cover opacity-65"
          src="https://customer-assets.emergentagent.com/job_brand-proposal-site/artifacts/x8yjquew_approaching-lower-east-side-new-york-city-in-black-and-white-part-2-SBV-347121318-HD.mp4"
          autoPlay muted loop playsInline preload="auto"
          aria-hidden
        />
        <div className="absolute inset-0 bg-gradient-to-b from-ink/40 via-ink/30 to-ink/85" aria-hidden />
        <div className="relative z-10 h-full container-edge flex flex-col justify-between py-16">
          <div className="overline text-cream/65">The House · By Application</div>
          <MaskReveal>
            <h2 className="font-display uppercase leading-[0.82] text-[14vw] md:text-[11vw]">
              We don&apos;t take<br/><span className="serif-italic font-serif normal-case">walk-ins</span>.
            </h2>
          </MaskReveal>
          <div className="flex items-end justify-between flex-wrap gap-4">
            <p className="font-serif text-xl md:text-2xl max-w-md text-cream/90">
              Apply, interview, partner. The doors open for a season; the carpet stays.
            </p>
            <Link to="/apply" className="btn-ink bg-cream text-ink border-cream hover:bg-transparent hover:text-cream" data-testid="closing-cta">
              Begin the application →
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

